import React, { useEffect, useMemo, useState } from 'react';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import { buildOrderMetrics } from '../utils/orderMetrics';
import './AdminDashboard.css';

const normalizeUser = (value) => {
  if (!value) return '';
  return String(value).trim();
};

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [groups, setGroups] = useState([]);

  const restBase = (localStorage.getItem('restBase') || '/rest/V1').trim() || '/rest/V1';
  const pickingStatus = (localStorage.getItem('pickingStatus') || 'picking').trim();

  useEffect(() => {
    const fetchAssignments = async () => {
      setLoading(true);
      setError('');
      try {
        const url =
          `${restBase}/orders?` +
          'searchCriteria[filter_groups][0][filters][0][field]=status' +
          `&searchCriteria[filter_groups][0][filters][0][value]=${encodeURIComponent(pickingStatus)}` +
          '&searchCriteria[filter_groups][0][filters][0][condition_type]=eq';

        const response = await apiFetch(url, { headers: getAuthHeaders() });
        if (!response.ok) {
          throw new Error(await response.text() || `Failed to load picking orders (${response.status})`);
        }
        const data = await response.json();
        const rawOrders = Array.isArray(data?.items) ? data.items : [];

        const detailed = await Promise.all(
          rawOrders.map(async (order) => {
            try {
              const detailRes = await apiFetch(`${restBase}/orders/${order.entity_id}`, {
                headers: getAuthHeaders(),
              });
              if (!detailRes.ok) throw new Error(await detailRes.text());
              return await detailRes.json();
            } catch (err) {
              console.warn('Failed to fetch order detail', order.entity_id, err);
              return null;
            }
          })
        );

        const map = new Map();
        detailed
          .filter(Boolean)
          .forEach((detail) => {
            const metrics = buildOrderMetrics(detail, { pickingStatus });
            const operatorName = normalizeUser(metrics.currentOperator) || 'Unassigned';
            const groupKey = operatorName.toLowerCase() || 'unassigned';

            if (!map.has(groupKey)) {
              map.set(groupKey, {
                operator: operatorName,
                orders: [],
                itemCount: 0,
                unitCount: 0,
              });
            }

            const group = map.get(groupKey);
            group.itemCount += metrics.itemCount;
            group.unitCount += metrics.unitCount;
            group.orders.push({
              incrementId: detail.increment_id,
              entityId: detail.entity_id,
              shipping: metrics.shippingLabel,
              createdAt: detail.created_at,
              status: detail.status,
              assignedAt: metrics.assignedAt,
              pickingAgeHours: metrics.pickingAgeHours,
              itemCount: metrics.itemCount,
              unitCount: metrics.unitCount,
              hasFrozen: metrics.hasFrozen,
              hasWarehouse: metrics.hasWarehouse,
              hasFresh: metrics.hasFresh,
              operationalStatus: metrics.operationalStatus,
            });
          });

        const sortedGroups = Array.from(map.values()).sort((a, b) => {
          if (a.operator.toLowerCase() === 'unassigned') return -1;
          if (b.operator.toLowerCase() === 'unassigned') return 1;
          if (a.orders.length === b.orders.length) {
            return a.operator.localeCompare(b.operator);
          }
          return b.orders.length - a.orders.length;
        });

        setGroups(sortedGroups);
      } catch (err) {
        console.error(err);
        setError(err.message || 'Failed to load assignments');
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [restBase, pickingStatus]);

  const totalOrders = useMemo(() => groups.reduce((acc, group) => acc + group.orders.length, 0), [groups]);

  return (
    <div className="admin-page">
      <div className="admin-shell">
        <header className="admin-header">
          <div>
            <h1>Operator Dashboard</h1>
            <p>{totalOrders} order{totalOrders === 1 ? '' : 's'} currently in Picking.</p>
          </div>
          <div className="admin-actions">
            <button className="admin-button" onClick={() => window.location.reload()}>
              Refresh
            </button>
          </div>
        </header>

        <LoadingOverlay show={loading} ariaLabel="Loading operator assignments" />
        {error && !loading && <div className="admin-state admin-state--error">{error}</div>}

        {!loading && !error && groups.length === 0 && (
          <div className="admin-state admin-state--empty">
            <h3>No orders in picking</h3>
            <p>Start picking orders to see operator assignments here.</p>
          </div>
        )}

        {!loading && !error && groups.length > 0 && (
          <div className="admin-grid">
            {groups.map((group) => (
              <section key={group.operator} className="admin-card">
                <header className="admin-card-header">
                  <div className="admin-card-title">
                    <h2>{group.operator}</h2>
                    <span className="admin-count">{group.orders.length}</span>
                  </div>
                  <small>{group.orders.length === 1 ? '1 order' : `${group.orders.length} orders`}</small>
                </header>
                <ul className="admin-order-list">
                  {group.orders.map((order) => (
                    <li key={order.incrementId}>
                      <div className="admin-order-main">
                        <span className="admin-order-id">#{order.incrementId}</span>
                        <span className="admin-order-ship">{order.shipping}</span>
                      </div>
                      <div className="admin-order-actions">
                        <button
                          className="admin-link"
                          onClick={() => {
                            localStorage.setItem('operatorOverrideIncrementId', order.incrementId);
                            window.location.href = `/picking/${order.incrementId}`;
                          }}
                        >
                          Open
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
